import React, { useState } from 'react';
import { toast as message } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabaseClient, supabaseCoreClient } from '../../../utils/supabase';
import type { ActionDialogProps } from '../types';
import EnhancedDialog from '../../EnhancedDialog';

interface ExistingMandate {
  id: number;
  ach_customer: number;
  mandate_status: string;
  account_holder_name: string;
  account_number_last4: string;
  bank_name: string | null;
}

export const AchChargeWithIntakeDialog: React.FC<ActionDialogProps> = ({
  isOpen,
  onClose,
  prefill,
}) => {
  const navigate = useNavigate();
  const merchantId = Number(prefill.merchant_id);

  // Mode: "new" or "existing"
  const [useExisting, setUseExisting] = useState(false);
  const [selectedMandateId, setSelectedMandateId] = useState<number | null>(null);

  // Bank details (new mode)
  const [accountHolderName, setAccountHolderName] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountType, setAccountType] = useState<'checking' | 'savings'>('checking');
  const [bankName, setBankName] = useState('');
  const [saveMandate, setSaveMandate] = useState(true);

  // Charge fields (both modes)
  const [amountDollars, setAmountDollars] = useState<string>('');
  const [description, setDescription] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [autoChargeOnApproval, setAutoChargeOnApproval] = useState(true);

  const { data: isApprover } = useQuery({
    queryKey: ['is_ach_approver'],
    queryFn: async () => {
      const { data } = await (supabaseCoreClient as any)
        .rpc('is_user_in_group', { p_group_name: 'ACH Charge Approvers' });
      return !!data;
    },
    staleTime: 60_000,
  });

  const { data: stripeMode } = useQuery({
    queryKey: ['ach_provider_config', 'stripe_mode'],
    queryFn: async () => {
      const { data } = await (supabaseClient as any)
        .from('ach_provider_config')
        .select('stripe_mode')
        .single();
      return (data?.stripe_mode as string) ?? 'test';
    },
    staleTime: 30_000,
    enabled: isOpen,
  });

  const { data: existingMandates } = useQuery<ExistingMandate[]>({
    queryKey: ['ach_mandates_for_merchant', merchantId],
    queryFn: async () => {
      const { data } = await supabaseClient
        .from('ach_mandate_view')
        .select('id, ach_customer, mandate_status, ach_customer_rel')
        .eq('mandate_status', 'active')
        .order('id', { ascending: false });

      if (!data) return [];

      const customerIds = data.map((m: any) => m.ach_customer);
      if (customerIds.length === 0) return [];

      const { data: customers } = await supabaseClient
        .from('ach_customer')
        .select('id, merchant, account_holder_name')
        .in('id', customerIds)
        .eq('merchant', merchantId);

      if (!customers || customers.length === 0) return [];

      const customerMap = new Map(customers.map((c: any) => [c.id, c]));

      const mandateIds = data.filter((m: any) => customerMap.has(m.ach_customer)).map((m: any) => m.id);
      const { data: mandatesWithPm } = await supabaseClient
        .from('ach_mandate')
        .select('id, ach_payment_method')
        .in('id', mandateIds);

      const pmIds = (mandatesWithPm || []).map((m: any) => m.ach_payment_method).filter(Boolean);
      const { data: pms } = await supabaseClient
        .from('ach_payment_method')
        .select('id, account_number_last4, bank_name')
        .in('id', pmIds);

      const pmMap = new Map((pms || []).map((p: any) => [p.id, p]));
      const mandatePmMap = new Map((mandatesWithPm || []).map((m: any) => [m.id, m.ach_payment_method]));

      return data
        .filter((m: any) => customerMap.has(m.ach_customer))
        .map((m: any) => {
          const cust = customerMap.get(m.ach_customer);
          const pmId = mandatePmMap.get(m.id);
          const pm = pmId ? pmMap.get(pmId) : null;
          return {
            id: m.id,
            ach_customer: m.ach_customer,
            mandate_status: m.mandate_status,
            account_holder_name: cust?.account_holder_name || 'Unknown',
            account_number_last4: pm?.account_number_last4 || '????',
            bank_name: pm?.bank_name || null,
          };
        });
    },
    staleTime: 10_000,
    enabled: isOpen && !!merchantId,
  });

  const hasExistingMandates = existingMandates && existingMandates.length > 0;
  const amount = parseFloat(amountDollars);
  const validAmount = !isNaN(amount) && amount > 0;

  const handleSubmit = async () => {
    if (!validAmount) return;
    setSubmitting(true);

    try {
      let requestBody: Record<string, unknown>;

      const approvalFields = !isApprover ? { auto_charge_on_approval: autoChargeOnApproval } : {};

      if (useExisting && selectedMandateId) {
        requestBody = {
          mode: 'existing',
          ach_mandate_id: selectedMandateId,
          amount_cents: Math.round(amount * 100),
          currency: 'usd',
          description: description || undefined,
          ...approvalFields,
        };
      } else {
        if (!accountHolderName || !routingNumber || !accountNumber) return;
        requestBody = {
          mode: 'new',
          merchant_id: merchantId,
          account_holder_name: accountHolderName,
          routing_number: routingNumber,
          account_number: accountNumber,
          account_type: accountType,
          bank_name: bankName || undefined,
          amount_cents: Math.round(amount * 100),
          currency: 'usd',
          description: description || undefined,
          save_mandate: saveMandate,
          ...approvalFields,
        };
      }

      const { data, error } = await supabaseClient.functions.invoke(
        'ach-charge-with-intake',
        { body: requestBody },
      );

      if (data?.error) throw new Error(data.error);
      if (error) throw error;

      const savedMsg = data.mandate_saved ? ' (mandate saved)' : ' (one-time)';
      const statusLabel = data.requires_approval ? 'pending approval' : (data.charge_status || data.status);
      if (data.requires_approval) {
        message.warning(`Charge ${statusLabel} — ${data.stripe_mode} mode${savedMsg}`);
      } else {
        message.success(`Charge ${statusLabel} — ${data.stripe_mode} mode${savedMsg}`);
      }
      resetForm();
      onClose();
      navigate(`/ACH_Charges/${data.ach_charge_attempt_id}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to process charge';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setUseExisting(false);
    setSelectedMandateId(null);
    setAccountHolderName('');
    setRoutingNumber('');
    setAccountNumber('');
    setAccountType('checking');
    setBankName('');
    setSaveMandate(true);
    setAmountDollars('');
    setDescription('');
  };

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  const canSubmit = useExisting
    ? !!selectedMandateId && validAmount
    : !!accountHolderName && !!routingNumber && !!accountNumber && validAmount;

  return (
    <EnhancedDialog
      isOpen={isOpen}
      onClose={handleClose}
      title="ACH Payment"
      style={{ width: 520 }}
      canOutsideClickClose={!submitting}
    >
      <div className="sky-dialog-body">
        <div className={`sky-callout ${stripeMode === 'live' ? 'sky-callout-danger' : 'sky-callout-warning'}`} style={{ marginBottom: 16 }}>
          {stripeMode === 'live'
            ? 'Live Mode — This will create a real charge.'
            : 'Test Mode — Stripe objects will be created in test mode.'}
        </div>

        {/* Payment method selector */}
        {hasExistingMandates && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
              <input
                type="checkbox"
                checked={useExisting}
                onChange={() => {
                  setUseExisting(!useExisting);
                  setSelectedMandateId(null);
                }}
              />
              Use saved payment method
            </label>
          </div>
        )}

        {useExisting ? (
          /* Existing mandate selector */
          <div className="sky-form-group">
            <label className="sky-form-label">Payment Method <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(required)</span></label>
            <select
              className="sky-field-input"
              value={selectedMandateId ?? ''}
              onChange={(e) => setSelectedMandateId(Number(e.target.value) || null)}
            >
              <option value="">Select a saved payment method...</option>
              {existingMandates?.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.account_holder_name} — ····{m.account_number_last4}
                  {m.bank_name ? ` (${m.bank_name})` : ''}
                </option>
              ))}
            </select>
          </div>
        ) : (
          /* New bank account fields */
          <>
            <div className="sky-form-group">
              <label className="sky-form-label">Account Holder Name <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(required)</span></label>
              <input
                className="sky-field-input"
                value={accountHolderName}
                onChange={(e) => setAccountHolderName(e.target.value)}
                placeholder="Name on bank account"
              />
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <div className="sky-form-group" style={{ flex: 1 }}>
                <label className="sky-form-label">Routing Number <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(required)</span></label>
                <input
                  className="sky-field-input"
                  value={routingNumber}
                  onChange={(e) => setRoutingNumber(e.target.value)}
                  placeholder="9-digit ABA"
                />
              </div>

              <div className="sky-form-group" style={{ flex: 1 }}>
                <label className="sky-form-label">Account Number <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(required)</span></label>
                <input
                  className="sky-field-input"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="Account number"
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <div className="sky-form-group" style={{ flex: 1 }}>
                <label className="sky-form-label">Account Type</label>
                <select
                  className="sky-field-input"
                  value={accountType}
                  onChange={(e) => setAccountType(e.target.value as 'checking' | 'savings')}
                >
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                </select>
              </div>

              <div className="sky-form-group" style={{ flex: 1 }}>
                <label className="sky-form-label">Bank Name</label>
                <input
                  className="sky-field-input"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12.5, marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={saveMandate}
                onChange={() => setSaveMandate(!saveMandate)}
              />
              Save payment method for future charges
            </label>
          </>
        )}

        <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '16px 0' }} />

        {/* Charge details */}
        <div className="sky-form-group">
          <label className="sky-form-label">Amount (USD) <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(required)</span></label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', fontSize: 13 }}>$</span>
            <input
              className="sky-field-input"
              type="number"
              value={amountDollars}
              onChange={(e) => setAmountDollars(e.target.value)}
              min="0.01"
              step="0.01"
              placeholder="0.00"
              style={{ paddingLeft: 24 }}
            />
          </div>
        </div>

        <div className="sky-form-group">
          <label className="sky-form-label">Description</label>
          <input
            className="sky-field-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this charge for?"
          />
        </div>

        {isApprover === false && (
          <>
            <div className="sky-callout" style={{ marginTop: 8, marginBottom: 8 }}>
              This charge requires approval before processing. An approver will review it.
            </div>
            <div className="sky-form-group">
              <label className="sky-form-label">Auto-charge on approval</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12.5 }}>
                <input
                  type="checkbox"
                  checked={autoChargeOnApproval}
                  onChange={() => setAutoChargeOnApproval((v) => !v)}
                />
                {autoChargeOnApproval ? 'Charge executes automatically when approved' : 'Charge must be executed manually after approval'}
              </label>
            </div>
          </>
        )}

        <div className="sky-callout" style={{ marginTop: 8 }}>
          ACH charges typically take 4-5 business days to settle.
        </div>
      </div>

      <div className="sky-dialog-footer">
        <button className="sky-btn sky-btn-ghost" onClick={handleClose} disabled={submitting}>Cancel</button>
        <button
          className="sky-btn sky-btn-primary"
          onClick={handleSubmit}
          disabled={submitting || !canSubmit}
        >
          {submitting ? <span className="sky-spinner" /> : null}
          {useExisting ? 'Charge' : (saveMandate ? 'Charge & Save' : 'Charge (One-Time)')}
        </button>
      </div>
    </EnhancedDialog>
  );
};
